import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CheckCircle, Clock, Copy01, Wallet02, XClose } from '@untitled-ui/icons-react'

import PaymentMethodPicker from '../components/PaymentMethodPicker'
import { BillingService } from '../billing.service'
import { formatIDR } from '../billing.format'
import PaymentQr from '@src/components/PaymentQr'
import { IS_PROD } from '@src/runtimeConfig'

// CONFIRM = the order summary + method picker; PAY = the in-portal QR/VA step.
// A gateway-funded subscribe renders its QRIS/VA HERE, exactly like Top up saldo
// (TopupFlow) — the customer never leaves for the hosted checkout page, so there
// is no gateway page whose "X" could bounce back as a false "payment success".
const STEP = { CONFIRM: 'confirm', PAY: 'pay' }

/**
 * "Confirm payment" — the dialog that opens when a plan card's button is pressed
 * (Figma 15195:146799).
 *
 * EVERY NUMBER COMES FROM THE SERVER. The modal calls `/billing/subscribe/preview`,
 * which runs the same `computeSubscriptionCharge()` that `/subscribe` will run a
 * moment later, so the figure the customer approves and the invoice they get
 * cannot disagree. Nothing here is recomputed in the browser.
 *
 * That matters because the Figma frame's own arithmetic does not hold together —
 * it is wrong in three separate places:
 *
 *   Subtotal Rp125.000 excludes the saldo line (200.000 − 75.000 prorate).
 *   Tax "11%" shows Rp19.000; 11% of 125.000 is 13.750 and of 200.000 is 22.000.
 *   Total Rp138.750 = 125.000 + 13.750 — i.e. it used the CORRECT tax, contradicting
 *   its own Tax row, and then dropped the −Rp40.000 saldo entirely.
 *
 * Under BR-PPN-03 the honest figure for that example is 85.000 remainder + 9.350
 * tax = Rp94.350. Design from Figma, numbers from the BRD.
 *
 * NOT RENDERED, deliberately:
 *   • "Adjustment / Prorate credit …" — proration does not exist. `changePlan`
 *     forfeits the old prepaid period outright, and BRD §2.3 puts mid-cycle
 *     proration out of scope for v1. A row we cannot compute is a row we cannot show.
 *   • "Tax" while BILLING_APPLY_TAX is off — the server returns 0, and a "Tax 11%"
 *     label above Rp0 reads as a bug. The row appears the moment tax is switched on.
 *   • A quota line. Subscribing no longer BUYS enquiry units: under the saldo model
 *     an enquiry is funded in Rupiah at the plan's rate, and what a plan hands over
 *     up front is its bonus quota. Figma's omission of that row was correct — the
 *     API used to demand `enquiry_count >= 1`, and that requirement was dropped
 *     rather than papered over here.
 */
function Row({ label, sub, value, tone }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-gray-light/900">{label}</p>
        {sub ? <p className="mt-0.5 text-xs text-gray-light/500">{sub}</p> : null}
      </div>
      <span
        className={`shrink-0 text-sm font-semibold ${tone === 'credit' ? 'text-success/700' : 'text-gray-light/900'}`}
      >
        {value}
      </span>
    </div>
  )
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  sub: PropTypes.string,
  value: PropTypes.string.isRequired,
  tone: PropTypes.string,
}
Row.defaultProps = { sub: null, tone: null }

function MethodTile({ children, selected, onClick, as }) {
  const Tag = as || 'button'
  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl border-2 bg-white p-3.5 text-left transition ${selected ? 'border-brand/600' : 'border-gray-light/200 hover:border-gray-light/300'
        }`}
    >
      {children}
    </Tag>
  )
}

MethodTile.propTypes = {
  children: PropTypes.node,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  as: PropTypes.string,
}
MethodTile.defaultProps = { children: null, selected: false, onClick: null, as: 'button' }

function ConfirmPaymentModal({ open, companyId, plan, wallet, onClose, onPaid }) {
  const [useSaldo, setUseSaldo] = useState(false)
  const [method, setMethod] = useState(null)
  const [preview, setPreview] = useState(null)
  const [quoting, setQuoting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // In-portal gateway payment step (upgrade/subscribe funded by VA/QRIS).
  const [step, setStep] = useState(STEP.CONFIRM)
  const [payResult, setPayResult] = useState(null)
  const [paid, setPaid] = useState(false)
  const [checking, setChecking] = useState(false)

  const saldo = Number(wallet?.balance_idr || 0)

  useEffect(() => {
    if (!open) return
    setPreview(null)
    setError(null)
    setUseSaldo(saldo > 0)
    setMethod(null)
    setStep(STEP.CONFIRM)
    setPayResult(null)
    setPaid(false)
  }, [open, plan?.id, saldo])

  // Re-quote on every priceable change. Debounced for the stepper; a sequence
  // guard keeps a slow early response from overwriting a fresh late one.
  const seq = useRef(0)
  useEffect(() => {
    if (!open || !companyId || !plan?.id) return undefined
    const mine = ++seq.current
    // The quote on screen stops being the quote being paid the moment any
    // priceable input changes; hold the pay button until the requote lands.
    setQuoting(true)
    const timer = setTimeout(() => {
      BillingService.previewSubscribe(companyId, {
        pricing_package_id: plan.id,
        // The method now comes from the picker, which resolves asynchronously.
        // Quote without one until it lands rather than sending [null] — the
        // gateway fee is the only part of the quote that depends on it.
        ...(method ? { payment_methods: [method] } : {}),
        use_saldo: useSaldo,
      })
        .then((res) => {
          if (mine !== seq.current) return
          setPreview(res?.data ?? res)
          setError(null)
          setQuoting(false)
        })
        .catch((err) => {
          if (mine !== seq.current) return
          setQuoting(false)
          setError(err?.response?.data?.message || err.message || 'Failed to calculate the invoice')
        })
    }, 250)
    return () => clearTimeout(timer)
  }, [open, companyId, plan?.id, method, useSaldo])

  const fullyFromSaldo = !!preview?.fully_from_saldo
  const saldoApplied = Number(preview?.saldo_applied_idr || 0)
  const taxAmount = Number(preview?.tax_amount_idr || 0)

  // Saldo XOR gateway (Dhimas 2026-09-23): saldo may pay a subscription only when
  // it covers the WHOLE bill — no saldo+gateway split. Below that the toggle is
  // disabled and the payment falls to a gateway. subtotal_idr is the package price
  // and does not depend on use_saldo, so it's a stable basis for the comparison.
  const subtotalIdr = Number(preview?.subtotal_idr || 0)
  const canUseSaldo = saldo > 0 && subtotalIdr > 0 && saldo >= subtotalIdr
  const saldoInsufficient = saldo > 0 && subtotalIdr > 0 && saldo < subtotalIdr

  // If saldo can't cover the whole bill, force the toggle off so the flow re-quotes
  // as a gateway payment (never a partial saldo + gateway remainder).
  useEffect(() => {
    if (preview && !canUseSaldo && useSaldo) setUseSaldo(false)
  }, [preview, canUseSaldo, useSaldo])

  // Gateway payment (STEP.PAY) fields — the in-portal QR / VA and the hosted
  // checkout fallback. Rendered client-side so the payer never leaves for the
  // gateway's own page.
  const pay = payResult?.payment || null
  const hasQr = Boolean(pay?.qr_string || pay?.qr_url)
  const checkoutUrl = payResult?.payment_url || null

  const submit = useCallback(async () => {
    if (!preview) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await BillingService.subscribe(companyId, {
        pricing_package_id: plan.id,
        ...(fullyFromSaldo ? {} : { payment_methods: [method] }),
        use_saldo: useSaldo,
      })
      const body = res?.data ?? res
      // Fully covered by saldo — nothing to pay; hand the terminal state upstream.
      if (body?.paid_from_saldo) {
        onPaid(body)
        return
      }
      // Gateway-funded: show the QR/VA IN-PORTAL and poll for settlement, rather
      // than redirecting the browser to the hosted checkout page. This is the fix
      // for "klik X di halaman Xendit/Midtrans malah muncul pembayaran berhasil":
      // there is no gateway page to land on — the QR is rendered in our own web.
      setPayResult(body)
      setStep(STEP.PAY)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to subscribe')
    } finally {
      setSubmitting(false)
    }
  }, [companyId, plan, method, useSaldo, fullyFromSaldo, preview, onPaid])

  // Poll the subscribe invoice while the gateway step is open, so a webhook-
  // confirmed payment flips the modal to "paid" (the subscription is activated
  // server-side on settlement) without the customer refreshing.
  const invoiceId = payResult?.invoice?.id || null
  const refreshStatus = useCallback(async () => {
    if (!companyId || !invoiceId) return
    setChecking(true)
    try {
      const res = await BillingService.getInvoiceDetail(companyId, invoiceId)
      const inv = res?.data ?? res
      if (inv?.status === 'paid') setPaid(true)
    } catch {
      /* transient — the next tick retries */
    } finally {
      setChecking(false)
    }
  }, [companyId, invoiceId])

  useEffect(() => {
    if (step !== STEP.PAY || paid || !invoiceId) return undefined
    const t = setInterval(refreshStatus, 5000)
    return () => clearInterval(t)
  }, [step, paid, invoiceId, refreshStatus])

  const copy = (text) => {
    if (!text) return
    try {
      navigator.clipboard?.writeText(String(text))
    } catch {
      /* ignore */
    }
  }

  if (!open || !plan) return null

  // Period is a property of the package now, not welded into the tier.
  const cycleLabel = Number(plan.period_days) === 365 ? 'Annual billing' : 'Monthly billing'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-light/700/60 p-4">
      {/* max-h + internal overflow so a tall payment-method list scrolls INSIDE
          the modal instead of scrolling the whole card off-screen (Everest
          #z94x8e25vd), matching the sibling confirm/change modals. */}
      <div className="max-h-[90vh] w-full max-w-[470px] overflow-y-auto rounded-2xl bg-white shadow-lg">
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-6">
          <h2 className="text-lg font-semibold text-gray-light/900">
            {step === STEP.PAY ? 'Complete payment' : 'Confirm payment'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-light/400 transition-colors hover:text-gray-light/700"
            aria-label="Close"
          >
            <XClose size={20} stroke="currentColor" />
          </button>
        </div>

        {step === STEP.PAY ? (
          <div className="flex flex-col gap-4 px-6 pb-6">
            {/* Status: waiting → paid (webhook-confirmed, polled below). */}
            <div className="flex items-center gap-3 rounded-lg border border-gray-light/200 p-4">
              <span
                className={`flex size-10 items-center justify-center rounded-full ${
                  paid ? 'bg-success/100 text-success/600' : 'bg-warning/100 text-warning/600'
                }`}
              >
                {paid ? <CheckCircle className="size-5" /> : <Clock className="size-5" />}
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-light/900">
                  {paid ? 'Payment received — subscription active' : 'Awaiting payment'}
                </p>
                <p className="text-sm text-gray-light/600">
                  {paid
                    ? 'Your plan is active now.'
                    : 'Your subscription activates automatically once the payment is verified.'}
                </p>
              </div>
            </div>

            {/* In-portal QRIS — scanned here, not on the gateway page. */}
            {!paid && hasQr ? <PaymentQr qrString={pay?.qr_string} qrUrl={pay?.qr_url} /> : null}

            {!paid && pay?.va_number ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-light/200 px-4 py-3">
                <span className="text-sm text-gray-light/700">
                  {pay.va_bank ? `${pay.va_bank} VA number` : 'VA number'}
                </span>
                <button
                  type="button"
                  onClick={() => copy(pay.va_number)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-light/900 hover:text-brand/700"
                >
                  {pay.va_number}
                  <Copy01 className="size-4 text-brand/600" />
                </button>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              {/* With an in-portal QR/VA the gateway page is a dev-only settlement
                  simulator; a redirect-only method keeps it as the way to pay. */}
              {!paid && checkoutUrl && (!(pay?.va_number || hasQr) || !IS_PROD) ? (
                <button
                  type="button"
                  onClick={() => window.open(checkoutUrl, '_blank', 'noopener')}
                  className="rounded-lg border border-gray-light/300 px-4 py-2.5 text-sm font-semibold text-gray-light/700"
                >
                  {pay?.va_number || hasQr ? 'Simulate payment (dev)' : 'Open payment page'}
                </button>
              ) : null}
              {!paid ? (
                <button
                  type="button"
                  onClick={refreshStatus}
                  disabled={checking}
                  className="rounded-lg border border-brand/300 bg-white px-4 py-2.5 text-sm font-semibold text-brand/700 disabled:opacity-50"
                >
                  {checking ? 'Checking…' : 'Check status'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => (paid ? onPaid(payResult) : onClose())}
                className="rounded-lg bg-brand/600 px-4 py-2.5 text-sm font-semibold text-white"
              >
                {paid ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        ) : (
        <div className="flex flex-col gap-5 px-6 pb-6">
          {/* ── Payment method ─────────────────────────────────────────────── */}
          <div className="overflow-hidden rounded-xl border border-gray-light/200">
            <p className="border-b border-gray-light/200 bg-gray-light/25 px-4 py-3 text-sm font-medium text-gray-light/900">
              Payment method
            </p>
            <div className="flex flex-col gap-3 p-3">
              {/* Saldo XOR gateway (Dhimas 2026-09-23, supersedes the 8-Aug
                  combination-for-subscriptions call): saldo may pay only when it
                  covers the WHOLE bill. Below that the toggle is disabled and the
                  customer tops up or pays by gateway — no saldo + tiny-remainder
                  split (which would eat a disproportionate PG fee). */}
              <MethodTile as="div" selected={useSaldo && canUseSaldo}>
                <span className="flex size-9 items-center justify-center rounded-lg border border-gray-light/200 text-brand/700">
                  <Wallet02 size={18} stroke="currentColor" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-gray-light/900">
                    Everest saldo
                  </span>
                  <span className="block text-xs text-gray-light/500">{formatIDR(saldo)}</span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useSaldo && canUseSaldo}
                  disabled={saldo <= 0 || !canUseSaldo}
                  onClick={() => setUseSaldo((v) => !v)}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-40 ${useSaldo && canUseSaldo ? 'bg-brand/600' : 'bg-gray-light/200'
                    }`}
                >
                  <span
                    className={`absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition-all ${useSaldo && canUseSaldo ? 'left-[22px]' : 'left-0.5'
                      }`}
                  />
                </button>
              </MethodTile>

              {/* Saldo can't cover the whole bill → it can't be used at all; tell
                  the customer to top up instead of silently charging full gateway. */}
              {saldoInsufficient ? (
                <p className="px-1 text-xs text-gray-light/500">
                  Your saldo is not enough to pay in full — top up first to pay with saldo, or choose a method below.
                </p>
              ) : null}

              {/* Once saldo covers the whole bill there is no gateway in the
                  flow, so offering a bank to transfer Rp0 into would be a lie. */}
              {fullyFromSaldo ? (
                <p className="px-1 pb-1 text-xs text-gray-light/500">
                  Your saldo covers the whole invoice — there is no virtual account to pay.
                </p>
              ) : (
                <PaymentMethodPicker
                  companyId={companyId}
                  amountIdr={preview ? Math.round(Number(preview.remainder_idr || 0)) : null}
                  value={method}
                  onChange={setMethod}
                />
              )}
            </div>
          </div>

          {/* ── Order summary ──────────────────────────────────────────────── */}
          <div>
            <p className="text-sm font-semibold text-gray-light/900">Order summary</p>

            <div className="mt-1 divide-y divide-gray-light/100">
              <Row
                label={`${plan.name} - ${cycleLabel}`}
                sub="Billed today, starts immediately"
                value={formatIDR(preview?.platform_fee_idr)}
              />

              {saldoApplied > 0 ? (
                <Row
                  label="Everest saldo"
                  sub="PPN was already charged at top up — it is not taxed again"
                  value={`−${formatIDR(saldoApplied)}`}
                  tone="credit"
                />
              ) : null}
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-gray-light/200">
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-gray-light/600">Subtotal</span>
                <span className="font-medium text-gray-light/900">
                  {formatIDR(preview?.subtotal_idr)}
                </span>
              </div>
              {!fullyFromSaldo && Number(preview?.app_fee_idr || 0) > 0 ? (
                <div className="flex items-center justify-between border-t border-gray-light/200 px-4 py-3 text-sm">
                  <span className="text-gray-light/600">App fee</span>
                  <span className="font-medium text-gray-light/900">
                    {formatIDR(preview?.app_fee_idr)}
                  </span>
                </div>
              ) : null}
              {taxAmount > 0 ? (
                <div className="flex items-center justify-between border-t border-gray-light/200 px-4 py-3 text-sm">
                  <span className="text-gray-light/600">
                    Tax
                    {saldoApplied > 0 ? (
                      <span className="ml-1 text-xs text-gray-light/500">
                        on {formatIDR(preview?.remainder_idr)}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-medium text-gray-light/900">{formatIDR(taxAmount)}</span>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-gray-light/200 bg-gray-light/25 px-4 py-3">
                <span className="text-sm font-semibold text-gray-light/900">Total due today</span>
                <span className="text-base font-semibold text-gray-light/900">
                  {preview ? formatIDR(preview.total_idr) : '—'}
                </span>
              </div>
            </div>
          </div>

          {error ? (
            <div className="rounded-lg border border-error/200 bg-error/50 px-3 py-2 text-sm text-error/700">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-3">
            {/* A gateway-funded payment needs a method; a fully-saldo one does not. */}
            <button
              type="button"
              onClick={submit}
              disabled={!preview || quoting || submitting || (!fullyFromSaldo && !method)}
              className="w-full rounded-xl bg-brand/600 px-4 py-3 text-sm font-semibold text-white shadow-shadows/shadow-xs transition hover:bg-brand/700 disabled:opacity-50"
            >
              {submitting
                ? 'Processing…'
                : !preview
                  ? 'Calculating…'
                  : fullyFromSaldo
                    ? 'Pay from saldo'
                    : 'Pay now'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full rounded-xl border border-gray-light/300 bg-white px-4 py-3 text-sm font-semibold text-gray-light/700 transition hover:bg-gray-light/25 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

ConfirmPaymentModal.propTypes = {
  open: PropTypes.bool,
  companyId: PropTypes.string,
  plan: PropTypes.object,
  wallet: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onPaid: PropTypes.func.isRequired,
}

ConfirmPaymentModal.defaultProps = {
  open: false,
  companyId: null,
  plan: null,
  wallet: null,
}

export default ConfirmPaymentModal
